/**
 * Spec №15.4 — the desk's owner-only maker section: the EXPORT KIT + the token
 * lifecycle (D-ST3 / D-ST4). Mounts in the Staff desk's Equipment tab (the spot the
 * 15.3 makernote reserved). Uses only shipped app-chrome classes (R1).
 *
 * The kit turns the agent record into host-installable form (four formats, all
 * pointing back at Kuvio MCP); the token is minted at download, shown ONCE, and
 * revocable/rotatable here. The bundle carries only a placeholder — never a secret
 * (D-EX2). Everything the section calls is owner/maker-gated server-side too.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../../lib/mcp-client";
import { getUser, getCurrentWorkspaceId } from "../../../lib/auth";

/** Unwrap a tool result, throwing on an isError / { error } payload. */
async function callTool<T>(name: string, args: Record<string, unknown>): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  const sc = r.structuredContent as (T & { error?: string }) | undefined;
  if (r.isError || (sc && sc.error)) throw new Error((sc?.error as string) || (r.content?.[0]?.text ?? "Request failed"));
  return (sc ?? ({} as T));
}

interface ExportFormats {
  skill_md?: string;
  cowork_plugin?: unknown;
  copilot_manifest?: unknown;
  agents_md?: string;
}
interface ExportResult {
  handle?: string;
  mcp_endpoint?: string;
  token_placeholder?: string;
  wall?: { head?: string; reason?: string };
  formats?: ExportFormats;
  bundle_files?: Array<{ path: string; content: string }>;
}
interface TokenRow {
  token_id: string;
  label: string | null;
  created_at: string;
  last_used_at: string | null;
  host_hint: string | null;
  status: string;
}

function isOwner(): boolean {
  const ws = getCurrentWorkspaceId();
  return getUser()?.workspaces.find((w) => w.id === ws)?.role === "owner";
}

function asText(v: unknown): string {
  return typeof v === "string" ? v : JSON.stringify(v, null, 2);
}

function download(name: string, content: string): void {
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const FORMAT_LABELS: Array<{ key: keyof ExportFormats; label: string; file: (h: string) => string }> = [
  { key: "skill_md", label: "SKILL.md (Claude Skills / Cowork)", file: () => "SKILL.md" },
  { key: "agents_md", label: "AGENTS.md (Claude Code)", file: () => "AGENTS.md" },
  { key: "cowork_plugin", label: "Cowork .plugin (MCP + skill)", file: (h) => `${h}.plugin.json` },
  { key: "copilot_manifest", label: "Copilot declarative-agent manifest", file: (h) => `${h}.copilot.json` },
];

export function ExportKitSection({ agentId, agentName }: { agentId: string; agentName: string }) {
  const owner = isOwner();
  const qc = useQueryClient();
  const [kit, setKit] = useState<ExportResult | null>(null);
  const [freshToken, setFreshToken] = useState<{ token: string; note: string } | null>(null);

  const tokensQ = useQuery({
    queryKey: ["agent-tokens", agentId],
    queryFn: () => callTool<{ tokens: TokenRow[] }>("list_agent_tokens", { agent_id: agentId }),
    enabled: owner,
  });
  const tokens = tokensQ.data?.tokens ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ["agent-tokens", agentId] });

  const exportM = useMutation({
    mutationFn: () => callTool<ExportResult>("export_agent", { agent_id: agentId }),
    onSuccess: (r) => setKit(r),
  });
  const mintM = useMutation({
    mutationFn: () => callTool<{ token: string; warning: string }>("mint_agent_token", { agent_id: agentId }),
    onSuccess: (r) => { setFreshToken({ token: r.token, note: r.warning }); invalidate(); },
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => callTool("revoke_agent_token", { token_id: id }),
    onSuccess: invalidate,
  });
  const rotateM = useMutation({
    mutationFn: (id: string) => callTool<{ token: string; warning: string }>("rotate_agent_token", { token_id: id }),
    onSuccess: (r) => { setFreshToken({ token: r.token, note: r.warning }); invalidate(); },
  });

  if (!owner) {
    return (
      <div className="makernote">
        In an interactive chat this agent is served through the Kuvio connector — nothing to install.
        The <b>headless</b> kit &amp; access tokens (for cron/CI/Copilot) are an <b>owner</b> maker
        action; ask a workspace owner to generate them.
      </div>
    );
  }

  const err = exportM.error ?? mintM.error ?? revokeM.error ?? rotateM.error ?? tokensQ.error;
  const rowBusy = (id: string) => (revokeM.isPending && revokeM.variables === id) || (rotateM.isPending && rotateM.variables === id);

  return (
    <div style={{ marginTop: 14 }}>
      <div className="makernote" style={{ marginBottom: 12 }}>
        <b>In an interactive chat, there&rsquo;s nothing to install.</b> Add the Kuvio connector to the
        host and {agentName} is served live — discover it in the roster and delegate to it, or
        <i> become</i> it. Same 11.2 wall, enforced server-side.
      </div>

      <div className="plane-eyebrow" style={{ marginBottom: 6 }}>Run headless · owner maker</div>
      <p className="tiny" style={{ color: "var(--color-text-tertiary)", margin: "0 0 10px" }}>
        For hosts with <b>no interactive chat</b> — a scheduled or CI runner, a Copilot manifest,
        bounded third-party access. Generate {agentName} as host-installable form (four formats, all
        pointing back at Kuvio MCP) and mint a revocable token below. Generated from the record,
        never hand-authored; edit the agent and regenerate.
      </p>

      {err && <div className="flowchip flowchip--warn" style={{ marginBottom: 10 }}><span style={{ flex: 1 }}>{(err as Error).message}</span></div>}

      <button data-variant="primary" disabled={exportM.isPending} onClick={() => exportM.mutate()}>
        {exportM.isPending ? "Generating…" : kit ? "Regenerate export" : "Generate export"}
      </button>

      {kit && (
        <div style={{ marginTop: 12 }}>
          <div className="verblist" style={{ marginBottom: 8 }}>
            <span className="verbchip">handle · {kit.handle}</span>
            <span className="verbchip">MCP · {kit.mcp_endpoint}</span>
            {kit.wall?.head && <span className="verbchip blocked">cannot {kit.wall.head}</span>}
          </div>
          {FORMAT_LABELS.map(({ key, label, file }) => {
            const content = kit.formats?.[key];
            if (content == null) return null;
            const text = asText(content);
            return (
              <div className="flowchip" key={key}>
                <span style={{ flex: 1 }}>{label}</span>
                <button className="tiny" onClick={() => void navigator.clipboard?.writeText(text)}>Copy</button>
                <button className="tiny" onClick={() => download(file(kit.handle ?? "agent"), text)}>Download</button>
              </div>
            );
          })}
          <div className="tiny" style={{ color: "var(--color-text-tertiary)", marginTop: 6 }}>
            The config carries a <code>{kit.token_placeholder}</code> placeholder — paste your minted token in its place.
          </div>
        </div>
      )}

      <div className="plane-eyebrow" style={{ margin: "18px 0 6px" }}>Headless access tokens · {agentName}</div>
      <p className="tiny" style={{ color: "var(--color-text-tertiary)", margin: "0 0 10px" }}>
        For unattended hosts only — a revocable, role-scoped machine-member token. Shown once; Kuvio
        stores only a hash. A runner using it acts <b>as this agent</b> — never wider than its 11.2
        roles, never able to approve money. (Interactive hosts don&rsquo;t need one; the connector
        carries the user&rsquo;s own auth.)
      </p>

      {freshToken && (
        <div className="card" style={{ marginBottom: 10, borderColor: "var(--color-accent)" }}>
          <div className="card__b">
            <div className="tiny" style={{ marginBottom: 6, color: "var(--color-text-secondary)" }}>{freshToken.note}</div>
            <code style={{ wordBreak: "break-all", display: "block", marginBottom: 8 }}>{freshToken.token}</code>
            <button data-variant="primary" className="tiny" onClick={() => void navigator.clipboard?.writeText(freshToken.token)}>Copy token</button>
            <button className="tiny" style={{ marginLeft: 8 }} onClick={() => setFreshToken(null)}>I&rsquo;ve stored it</button>
          </div>
        </div>
      )}

      <button data-variant="primary" disabled={mintM.isPending} onClick={() => mintM.mutate()}>
        {mintM.isPending ? "Minting…" : "Mint a token"}
      </button>

      <div style={{ marginTop: 10 }}>
        {tokens.length === 0 ? (
          <div className="muted tiny">No tokens issued yet.</div>
        ) : tokens.map((t) => (
          <div className="flowchip" key={t.token_id}>
            <span style={{ flex: 1 }}>
              <span className={`statuschip${t.status === "revoked" ? " statuschip--neutral" : ""}`}>{t.status}</span>
              {t.label ? ` ${t.label}` : ` ${t.token_id}`}
              <span className="tiny" style={{ color: "var(--color-text-tertiary)", marginLeft: 8 }}>
                {t.last_used_at ? `last used ${t.last_used_at}` : "never used"}{t.host_hint ? ` · ${t.host_hint}` : ""}
              </span>
            </span>
            {t.status !== "revoked" && (
              <>
                <button className="tiny" disabled={rowBusy(t.token_id)} onClick={() => rotateM.mutate(t.token_id)}>Rotate</button>
                <button className="tiny" disabled={rowBusy(t.token_id)} onClick={() => revokeM.mutate(t.token_id)}>Revoke</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
